// ───────────────────────────────────────────────────────────────────────────
// Project Safety Hold + Receivership PDF Panel
// Renders on the Project Detail view. Two clean lanes:
//   1. Crew Safety Hold:    Engage / Release the 48-hr clock + history timeline
//   2. Receivership PDFs:   Standard + Expedited court reports (Jim's endpoint spec)
//
// Wiring rules (Jim, May 17 2026):
//   • Endpoint scoping → /api/receivership/generate-standard | -expedited
//   • State locking    → when isHolding=true, the email-notify checkbox is
//                         disabled & a banner explains the deferred-comms guard
//   • Access control   → calls are admin-only; this panel only renders when
//                         the parent grants render permission (dashboardRole)
// ───────────────────────────────────────────────────────────────────────────
import { useEffect, useState } from "react";

const BACKEND_URL = "";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch (_e) {
    return iso;
  }
};

export const ProjectSafetyAndReceivership = ({ projectId, projectAddress, dashboardRole: propRole }) => {
  const [status, setStatus] = useState(null);    // { is_paused, freeze_history, ... }
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState("");
  const [showEngage, setShowEngage] = useState(false);
  const [showRelease, setShowRelease] = useState(false);
  const [engageNotes, setEngageNotes] = useState("");
  const [engageKeyword, setEngageKeyword] = useState("");
  const [releaseReason, setReleaseReason] = useState("");
  const [remediationProof, setRemediationProof] = useState("");
  const [receiverSig, setReceiverSig] = useState("");
  const [notifyReceiver, setNotifyReceiver] = useState(false);
  const [generating, setGenerating] = useState("");  // "standard" | "expedited"
  const [resolvedRole, setResolvedRole] = useState(propRole || "");

  const canEngageRelease = ["admin", "ceo", "coo"].includes(
    (resolvedRole || "").toLowerCase()
  );

  const isHolding = !!status?.is_paused;

  const loadStatus = async () => {
    try {
      const r = await fetch(
        `${BACKEND_URL}/api/admin/projects/${projectId}/safety-status`,
        { credentials: "include" }
      );
      if (r.ok) setStatus(await r.json());
    } catch (_e) {
      // silent — keep last status
    }
    setLoading(false);
  };

  useEffect(() => {
    if (projectId) loadStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  useEffect(() => {
    // Self-resolve role if not provided by parent — access-control guard.
    if (propRole) { setResolvedRole(propRole); return; }
    (async () => {
      try {
        const r = await fetch(`${BACKEND_URL}/api/auth/me`, { credentials: "include" });
        if (r.ok) {
          const u = await r.json();
          setResolvedRole(u.dashboard_role || u.role || "");
        }
      } catch (_e) { /* role stays empty -> buttons hidden */ }
    })();
  }, [propRole]);

  const showFlash = (msg) => {
    setFlash(msg);
    setTimeout(() => setFlash(""), 4500);
  };

  const engageFreeze = async () => {
    if (!engageKeyword.trim() && !engageNotes.trim()) {
      alert("Provide a trigger keyword (e.g., 'mold', 'hazmat') or notes describing the hazard.");
      return;
    }
    setBusy(true);
    try {
      const r = await fetch(
        `${BACKEND_URL}/api/admin/projects/${projectId}/safety-freeze`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            trigger_keywords: engageKeyword ? [engageKeyword.trim()] : [],
            notes: engageNotes,
          }),
        }
      );
      const data = await r.json();
      if (r.ok && data.ok) {
        showFlash("✅ Safety Hold engaged. 48-hr clock paused. Legal notice email queued.");
        setShowEngage(false);
        setEngageKeyword("");
        setEngageNotes("");
        await loadStatus();
      } else {
        showFlash(`⚠️ Could not engage: ${data.detail || data.error || "unknown error"}`);
      }
    } catch (e) {
      showFlash(`⚠️ Network error: ${e.message}`);
    }
    setBusy(false);
  };

  const releaseFreeze = async () => {
    if (!releaseReason.trim()) {
      alert("A release reason is required for the audit trail.");
      return;
    }
    setBusy(true);
    try {
      const r = await fetch(
        `${BACKEND_URL}/api/admin/projects/${projectId}/safety-unfreeze`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            reason: releaseReason,
            remediation_proof_path: remediationProof,
          }),
        }
      );
      const data = await r.json();
      if (r.ok && data.ok) {
        showFlash("✅ Safety Hold released. Clock resumed. History stamped for audit.");
        setShowRelease(false);
        setReleaseReason("");
        setRemediationProof("");
        await loadStatus();
      } else {
        showFlash(`⚠️ Could not release: ${data.detail || data.error || "unknown error"}`);
      }
    } catch (e) {
      showFlash(`⚠️ Network error: ${e.message}`);
    }
    setBusy(false);
  };

  const generateReceivershipPDF = async (mode) => {
    setGenerating(mode);
    try {
      const r = await fetch(`${BACKEND_URL}/api/receivership/generate-${mode}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          project_id: projectId,
          receiver_signature: receiverSig,
          // STATE LOCKING — never send while on hold
          notify_receiver: notifyReceiver && !isHolding,
        }),
      });
      if (!r.ok) {
        const data = await r.json().catch(() => ({}));
        showFlash(`⚠️ PDF generation failed: ${data.detail || r.statusText}`);
      } else {
        const blob = await r.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `MF-Receivership-Report-${mode}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
        const emailStatus = r.headers.get("X-Email-Status") || "n/a";
        const holdAtGen = r.headers.get("X-Safety-Hold") === "true";
        showFlash(
          `✅ ${mode.toUpperCase()} Receivership Report generated. ` +
          (holdAtGen ? "⏸ Safety hold active — receiver email deferred." : `Email: ${emailStatus}`)
        );
      }
    } catch (e) {
      showFlash(`⚠️ Network error: ${e.message}`);
    }
    setGenerating("");
  };

  if (loading) {
    return (
      <div style={panelStyle} data-testid="safety-receivership-panel-loading">
        <p style={{ color: "#94A3B8", fontSize: 13 }}>Loading safety status…</p>
      </div>
    );
  }

  return (
    <div style={panelStyle} data-testid="safety-receivership-panel">
      {/* ─── HEADER ─── */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10, marginBottom: 14 }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 1.4, color: "#5AC8D8", textTransform: "uppercase" }}>
            Safety + Receivership
          </div>
          <h3 style={{ margin: "4px 0 2px", color: "#fff", fontSize: 17, fontWeight: 800, letterSpacing: "-0.01em" }}>
            Crew Hold & Court Reporting
          </h3>
          <p style={{ margin: 0, fontSize: 10.5, color: "#7E92A8", letterSpacing: 0.3 }}>
            Admin Routing Matrix · Restricted Access Active
          </p>
        </div>
        <span
          data-testid="safety-status-badge"
          style={{
            padding: "6px 12px",
            background: isHolding ? "rgba(239,68,68,0.18)" : "rgba(0,220,120,0.14)",
            border: `1px solid ${isHolding ? "rgba(239,68,68,0.55)" : "rgba(0,220,120,0.45)"}`,
            color: isHolding ? "#FCA5A5" : "#86EFAC",
            borderRadius: 999,
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: 0.5,
            textTransform: "uppercase",
          }}
        >
          {isHolding ? "⏸ Holding · 48-hr Clock Paused" : "✓ Clock Running"}
        </span>
      </div>

      {flash && (
        <div data-testid="safety-flash"
             style={{ marginBottom: 12, padding: "9px 12px", background: "rgba(0,200,200,0.10)", border: "1px solid rgba(0,200,200,0.30)", borderRadius: 8, fontSize: 12.5, color: "#E6EEF8" }}>
          {flash}
        </div>
      )}

      {/* ─── HOLD CONTROLS ─── */}
      <div style={blockStyle}>
        <div style={blockHeaderStyle}>🛡️ Crew Safety Hold</div>

        {isHolding ? (
          <>
            <p style={{ color: "#FCA5A5", fontSize: 13, margin: "0 0 6px" }}>
              Reason: <strong>{status?.pause_reason || "—"}</strong>
            </p>
            <p style={{ color: "#94A3B8", fontSize: 12, margin: "0 0 12px" }}>
              Paused at: {fmtDate(status?.paused_at)}
            </p>
            {canEngageRelease ? (
              <button
                onClick={() => setShowRelease(true)}
                disabled={busy}
                data-testid="safety-release-btn"
                style={primaryBtn("#00DC78", "#5AC8D8")}
              >
                Release Safety Hold →
              </button>
            ) : (
              <p style={{ color: "#94A3B8", fontSize: 12 }}>Only CEO / COO can release.</p>
            )}
          </>
        ) : (
          <>
            <p style={{ color: "#94A3B8", fontSize: 13, margin: "0 0 12px", lineHeight: 1.55 }}>
              Engage to manually pause the 48-hr clock. Outbound notifications & receivership
              emails will be deferred until released. Use this when crew flags an on-site hazard
              (mold, asbestos, structural, biohazard, etc.).
            </p>
            {canEngageRelease ? (
              <button
                onClick={() => setShowEngage(true)}
                disabled={busy}
                data-testid="safety-engage-btn"
                style={primaryBtn("#F472B6", "#A78BFA")}
              >
                ⏸ Engage Safety Hold
              </button>
            ) : (
              <p style={{ color: "#94A3B8", fontSize: 12 }}>Only CEO / COO can engage.</p>
            )}
          </>
        )}

        {/* History timeline */}
        {(status?.freeze_history?.length || 0) > 0 && (
          <div style={{ marginTop: 14, paddingTop: 12, borderTop: "1px solid rgba(255,255,255,0.06)" }}>
            <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: 1.2, color: "#94A3B8", textTransform: "uppercase", marginBottom: 8 }}>
              Hold History
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }} data-testid="safety-history">
              {(status.freeze_history || []).slice().reverse().slice(0, 6).map((h, i) => (
                <div key={i} style={historyItemStyle}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 6 }}>
                    <span style={{ fontSize: 11, color: h.event === "frozen" ? "#FCA5A5" : "#86EFAC", fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase" }}>
                      {h.event === "frozen" ? "⏸ Frozen" : "▶ Released"}
                    </span>
                    <span style={{ fontSize: 11, color: "#94A3B8" }}>{fmtDate(h.at)}</span>
                  </div>
                  {h.reason && <p style={{ margin: "4px 0 0", fontSize: 12.5, color: "#E6EEF8", lineHeight: 1.4 }}>{h.reason}</p>}
                  {h.by && <p style={{ margin: "2px 0 0", fontSize: 11, color: "#94A3B8" }}>by {h.by}{h.role ? ` · ${h.role}` : ""}</p>}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ─── RECEIVERSHIP PDF TRIGGERS ─── */}
      <div style={{ ...blockStyle, marginTop: 12 }}>
        <div style={blockHeaderStyle}>⚖️ Receivership Report Generation</div>
        <p style={{ color: "#94A3B8", fontSize: 13, margin: "0 0 12px", lineHeight: 1.55 }}>
          Court-grade interim report (Texas Civil Practice & Remedies Code). Pulls all
          watermarked photos, court-approved invoices, and motion-ordered line items
          for <strong>{projectAddress || "this project"}</strong>.
        </p>

        <div style={{ marginBottom: 10 }}>
          <label style={fieldLabel}>Receiver Signature (typed)</label>
          <input
            type="text"
            value={receiverSig}
            onChange={(e) => setReceiverSig(e.target.value)}
            placeholder="e.g., /s/ Jane Receiver, Court-Appointed Receiver"
            data-testid="receiver-signature-input"
            style={fieldInput}
          />
        </div>

        <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14, cursor: isHolding ? "not-allowed" : "pointer", opacity: isHolding ? 0.5 : 1 }} data-testid="notify-receiver-label">
          <input
            type="checkbox"
            checked={notifyReceiver && !isHolding}
            disabled={isHolding}
            onChange={(e) => setNotifyReceiver(e.target.checked)}
            data-testid="notify-receiver-checkbox"
          />
          <span style={{ fontSize: 12.5, color: "#E6EEF8" }}>
            Email PDF to the receiver of record after generation
          </span>
        </label>

        {isHolding && (
          <div style={{ marginBottom: 12, padding: "9px 12px", background: "rgba(239,68,68,0.10)", border: "1px solid rgba(239,68,68,0.30)", borderRadius: 8, fontSize: 12, color: "#FCA5A5" }}>
            ⏸ Safety Hold active — outbound receiver emails are deferred. The server marks the email queue with{" "}
            <code style={{ background: "rgba(0,0,0,0.35)", color: "#FCA5A5", padding: "1px 6px", borderRadius: 4, fontSize: 11, fontFamily: "monospace" }}>
              X-Email-Status: skipped_safety_hold
            </code>
            {" "}until release. The PDF will still generate for in-court service.
          </div>
        )}

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button
            onClick={() => generateReceivershipPDF("standard")}
            disabled={generating !== ""}
            data-testid="receivership-standard-btn"
            style={primaryBtn("#5AC8D8", "#00DC78")}
          >
            {generating === "standard" ? "Building…" : "📄 Generate Standard Report"}
          </button>
          <button
            onClick={() => generateReceivershipPDF("expedited")}
            disabled={generating !== ""}
            data-testid="receivership-expedited-btn"
            style={primaryBtn("#FFB347", "#F472B6")}
          >
            {generating === "expedited" ? "Building…" : "🚨 Generate Expedited Filing"}
          </button>
        </div>
      </div>

      {/* ─── ENGAGE MODAL ─── */}
      {showEngage && (
        <ModalShell title="Engage Crew Safety Hold" onClose={() => setShowEngage(false)} testId="engage-modal">
          <label style={fieldLabel}>Trigger keyword (optional, single word)</label>
          <input
            type="text"
            value={engageKeyword}
            onChange={(e) => setEngageKeyword(e.target.value)}
            placeholder="e.g., mold, asbestos, hazmat, structural"
            data-testid="engage-keyword-input"
            style={fieldInput}
          />
          <label style={{ ...fieldLabel, marginTop: 10 }}>Notes / hazard description</label>
          <textarea
            value={engageNotes}
            onChange={(e) => setEngageNotes(e.target.value)}
            rows={3}
            placeholder="Describe what crew observed. This goes into the legal notice + audit trail."
            data-testid="engage-notes-input"
            style={{ ...fieldInput, fontFamily: "inherit" }}
          />
          <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
            <button onClick={() => setShowEngage(false)} style={secondaryBtn} data-testid="engage-cancel">Cancel</button>
            <button onClick={engageFreeze} disabled={busy} style={primaryBtn("#F472B6", "#A78BFA")} data-testid="engage-confirm">
              {busy ? "Engaging…" : "Engage Hold"}
            </button>
          </div>
        </ModalShell>
      )}

      {/* ─── RELEASE MODAL ─── */}
      {showRelease && (
        <ModalShell title="Release Crew Safety Hold" onClose={() => setShowRelease(false)} testId="release-modal">
          <label style={fieldLabel}>Reason for release</label>
          <textarea
            value={releaseReason}
            onChange={(e) => setReleaseReason(e.target.value)}
            rows={3}
            placeholder="e.g., 'Correction completed by ABC Mold Co., clearance letter on file.'"
            data-testid="release-reason-input"
            style={{ ...fieldInput, fontFamily: "inherit" }}
          />
          <label style={{ ...fieldLabel, marginTop: 10 }}>Correction proof URL / path (optional)</label>
          <input
            type="text"
            value={remediationProof}
            onChange={(e) => setRemediationProof(e.target.value)}
            placeholder="URL to clearance letter or photo proof"
            data-testid="release-proof-input"
            style={fieldInput}
          />
          <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
            <button onClick={() => setShowRelease(false)} style={secondaryBtn} data-testid="release-cancel">Cancel</button>
            <button onClick={releaseFreeze} disabled={busy} style={primaryBtn("#00DC78", "#5AC8D8")} data-testid="release-confirm">
              {busy ? "Releasing…" : "Release Hold"}
            </button>
          </div>
        </ModalShell>
      )}

      {/* ─── TUTSA TRADE-SECRET NOTICE ─── */}
      <div data-testid="tutsa-notice" style={tutsaNoticeStyle}>
        <p style={{ fontSize: 10.5, color: "#94A3B8", lineHeight: 1.65, margin: 0 }}>
          <strong style={{ color: "#5AC8D8", letterSpacing: 0.4 }}>
            PROPRIETARY NOTICE &amp; PROPERTY OF MOOVIN FORWARD LLC:
          </strong>
          <br />
          This interface, including the proprietary 18-keyword automated safety-hold logic,
          dynamic labor matrix multipliers (Swift / Heavy), and court-grade reporting architecture,
          constitutes a protected Trade Secret of Moovin Forward LLC. Unauthorized replication,
          adaptation, reverse-engineering, or extraction of this operational workflow for commercial
          use is strictly prohibited under the{" "}
          <strong style={{ color: "#E6EEF8" }}>Texas Uniform Trade Secrets Act (TUTSA)</strong> and
          federal law. Access is restricted to authorized personnel only. All session interactions,
          state changes, and report generations are permanently audited and digitally logged.
        </p>
      </div>
    </div>
  );
};


// ─── styles ──────────────────────────────────────────────────────────────────
const panelStyle = {
  marginTop: 18,
  padding: "18px 20px",
  background: "linear-gradient(180deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.02) 100%)",
  border: "1px solid rgba(255,255,255,0.08)",
  borderRadius: 12,
  color: "#E6EEF8",
};
const blockStyle = {
  padding: "14px 16px",
  background: "rgba(255,255,255,0.03)",
  border: "1px solid rgba(255,255,255,0.06)",
  borderRadius: 10,
};
const blockHeaderStyle = {
  fontSize: 13,
  fontWeight: 800,
  color: "#fff",
  marginBottom: 10,
  letterSpacing: "-0.005em",
};
const historyItemStyle = {
  padding: "9px 12px",
  background: "rgba(255,255,255,0.03)",
  border: "1px solid rgba(255,255,255,0.05)",
  borderRadius: 7,
};
const tutsaNoticeStyle = {
  marginTop: 22,
  padding: "13px 16px",
  borderTop: "1px solid rgba(255,255,255,0.08)",
  background: "rgba(2, 8, 20, 0.55)",
  borderRadius: 8,
};
const fieldLabel = {
  display: "block",
  fontSize: 11,
  fontWeight: 700,
  color: "#94A3B8",
  textTransform: "uppercase",
  letterSpacing: 0.6,
  marginBottom: 5,
};
const fieldInput = {
  width: "100%",
  padding: "9px 11px",
  borderRadius: 7,
  border: "1px solid rgba(255,255,255,0.12)",
  background: "rgba(255,255,255,0.04)",
  color: "#fff",
  fontSize: 13,
  boxSizing: "border-box",
};
const primaryBtn = (c1, c2) => ({
  padding: "10px 16px",
  border: "none",
  borderRadius: 8,
  background: `linear-gradient(135deg, ${c1} 0%, ${c2} 100%)`,
  color: "#030B1A",
  fontSize: 13,
  fontWeight: 800,
  cursor: "pointer",
  letterSpacing: 0.1,
});
const secondaryBtn = {
  padding: "10px 16px",
  border: "1px solid rgba(255,255,255,0.14)",
  borderRadius: 8,
  background: "rgba(255,255,255,0.04)",
  color: "#E6EEF8",
  fontSize: 13,
  fontWeight: 700,
  cursor: "pointer",
};

const ModalShell = ({ title, onClose, testId, children }) => (
  <div
    data-testid={`${testId}-overlay`}
    onClick={onClose}
    style={{ position: "fixed", inset: 0, background: "rgba(2,8,20,0.85)", backdropFilter: "blur(8px)", zIndex: 10000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
  >
    <div
      data-testid={testId}
      onClick={(e) => e.stopPropagation()}
      style={{ width: "100%", maxWidth: 480, background: "linear-gradient(180deg, #0A1929 0%, #050E1F 100%)", border: "1px solid rgba(0,200,200,0.25)", borderRadius: 12, padding: "20px 22px", color: "#E6EEF8" }}
    >
      <h3 style={{ margin: "0 0 14px", fontSize: 17, color: "#fff", fontWeight: 800 }}>{title}</h3>
      {children}
    </div>
  </div>
);

export default ProjectSafetyAndReceivership;
